import React, { useState, useEffect } from "react";
import PageTitle from "../../components/screens/page-title";
import styles from "./calls.module.css";
import Button from "../../components/ui/button";
import Dropdown from "../../components/ui/dropdown";
import Input from "../../components/ui/input";

import { useTwilioCall } from "../../hooks/useTwilioCall";
import TwilioCallLogs from '../../components/twilio-call-handler/TwilioCallLogs';
import AgentSelectionModal from '../../components/modals/AgentSelectionModal';
import { getNonExpedienteContacts } from "../../services/apiService";
import { hasTwilioAccess } from "../../utils/userRole";
import { API_BASE_URL } from "../../config";
import usePageTitle from "../../hooks/usePageTitle";
import { useUser } from '../../contexts/user-context';

const CallsPage = () => {
  usePageTitle('Llamadas');
  const { user, usuarioId } = useUser();
  const [selectedContact, setSelectedContact] = useState(null);
  const [customNumber, setCustomNumber] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [contactsList, setContactsList] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [callStartTime, setCallStartTime] = useState(null);
  
  // Estados para llamadas internas entre agentes
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  
  // 🚫 Verificar acceso a Twilio
  const userHasTwilioAccess = hasTwilioAccess();
  
  // Integración con Twilio
  const { isDeviceReady, hasActiveCall, activeCallInfo, call: makeCall } = useTwilioCall();

  // Cargar contactos no-expedientes
  useEffect(() => {
    const loadContacts = async () => {
      try {
        setLoadingContacts(true);
        const contacts = await getNonExpedienteContacts();
        console.log('📋 Contactos no-expedientes obtenidos:', contacts);
        
        const formattedContacts = [];
        
        // Agregar la opción "Otro número..." como primera opción
        formattedContacts.push({ value: "otro", label: "Otro número..." });
        
        // Agregar contactos obtenidos de la API
        if (contacts && contacts.length > 0) {
          contacts.forEach(contact => {
            formattedContacts.push({
              value: contact.id,
              label: `${contact.nombre} ${contact.nombreEntidad ? `(${contact.nombreEntidad})` : `(${contact.tipo})`}`,
              phone: contact.telefono,
              tipo: contact.tipo,
              nombreEntidad: contact.nombreEntidad
            });
          });
        }
        
        setContactsList(formattedContacts);
        console.log('📋 Lista de contactos formateada:', formattedContacts);
      } catch (error) {
        console.error("Error loading contacts:", error);
        // Fallback a solo "Otro número..."
        setContactsList([{ value: "otro", label: "Otro número..." }]);
      } finally {
        setLoadingContacts(false);
      }
    };

    loadContacts();
  }, []);

  // Timer para la duración de la llamada
  useEffect(() => {
    let interval = null;
    if (hasActiveCall && callStartTime) {
      interval = setInterval(() => {
        setCallDuration(Math.floor((Date.now() - callStartTime) / 1000));
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [hasActiveCall, callStartTime]);

  // Función para manejar la selección de contacto
  const handleContactSelect = (contact) => {
    setSelectedContact(contact);
    if (contact && contact.value === "otro") {
      setShowCustomInput(true);
      setCustomNumber("");
    } else {
      setShowCustomInput(false);
      setCustomNumber("");
    }
  };

  // Función para obtener el contacto actual (combina seleccionado + activo)
  const getCurrentContact = () => {
    // Si hay una llamada activa, usar esa información primero
    if (hasActiveCall && activeCallInfo) {
      const contactInfo = activeCallInfo.contactInfo;
      const from = activeCallInfo.from;
      
      if (contactInfo) {
        return {
          name: contactInfo.name || contactInfo.nombre || from,
          phoneNumber: from,
          contactName: contactInfo.name || contactInfo.nombre
        };
      } else {
        return {
          name: from,
          phoneNumber: from
        };
      }
    }
    
    // Si no hay llamada activa, usar el contacto seleccionado
    if (showCustomInput && customNumber) {
      return {
        name: "Número personalizado",
        phoneNumber: customNumber
      };
    }
    
    if (selectedContact && selectedContact.value !== "otro") {
      return {
        name: selectedContact.label,
        phoneNumber: selectedContact.phone,
        contactName: selectedContact.label
      };
    }
    
    return null;
  };

  // Función para formatear número de teléfono con prefijo +34
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return null;
    
    // Si ya tiene prefijo +, no hacer nada
    if (phoneNumber.startsWith('+')) {
      return phoneNumber;
    }
    
    // Si empieza con 00, reemplazar por +
    if (phoneNumber.startsWith('00')) {
      return '+' + phoneNumber.substring(2);
    }
    
    // Si no tiene prefijo, añadir +34 (España)
    return '+34' + phoneNumber;
  };

  // Función para formatear la duración
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Función para iniciar llamada
  const handleStartCall = async () => {
    const currentContact = getCurrentContact();
    if (!currentContact || !isDeviceReady) {
      console.error('No se puede realizar la llamada: contacto no válido o dispositivo no listo');
      return;
    }
    
    setCallStartTime(Date.now());
    
    try {
      // Formatear número con prefijo antes de llamar
      const formattedNumber = formatPhoneNumber(currentContact.phoneNumber);
      console.log('📞 Llamando a:', formattedNumber, '(original:', currentContact.phoneNumber, ')');
      
      // Realizar llamada usando Twilio - El estado se maneja en el panel flotante
      await makeCall(formattedNumber);
      console.log('Llamada iniciada correctamente');
    } catch (error) {
      console.error('Error al iniciar la llamada:', error);
      setCallStartTime(null);
    }
  };

  // Función para terminar llamada
  const handleEndCall = () => {
    setCallDuration(0);
    setCallStartTime(null);
    // El colgado se maneja desde el panel flotante con hangUp()
  };

  // Función para abrir modal de llamadas internas
  const handleOpenAgentModal = async () => {
    setLoadingAgents(true);
    setShowAgentModal(true);
    
    try {
      console.log('🔍 DEBUG - Usuario actual en calls:', user);
      
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/twilio/agents/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Error al obtener agentes');
      }
      
      const agents = await response.json();
      console.log('📋 Agentes disponibles:', agents);
      
      // Filtrar para excluir al usuario actual
      const agentsFiltered = agents.filter(agent => {
        const shouldInclude = usuarioId && agent.id !== parseInt(usuarioId);
        const canReceiveInternalCall = agent.conectado !== false;
        console.log(`🔍 Agente ${agent.nombre} (id: ${agent.id}) - usuarioId: ${usuarioId} - conectado: ${canReceiveInternalCall} - incluir: ${shouldInclude && canReceiveInternalCall}`);
        return shouldInclude && canReceiveInternalCall;
      });
      
      console.log('📋 Agentes después de filtrar:', agentsFiltered);
      setAvailableAgents(agentsFiltered);
    } catch (error) {
      console.error('Error al cargar agentes:', error);
      setAvailableAgents([]);
    } finally {
      setLoadingAgents(false);
    }
  };

  // Función para manejar llamada interna a agente
  const handleInternalCall = async (agent) => {
    try {
      console.log('📞 Iniciando llamada interna a:', agent);
      
      // Construir el identificador del agente (sin prefijo "client:")
      const username = agent.email.split('@')[0];
      
      console.log('📞 Llamando a:', username);
      
      // Realizar llamada interna al agente
      await makeCall(username);
      
      // Cerrar el modal
      setShowAgentModal(false);
      
    } catch (error) {
      console.error('❌ Error en llamada interna:', error);
      alert('Error al realizar la llamada interna');
    }
  };

  return (
    <div className={styles.callsPage}>
      <PageTitle
        title="Llamadas"
        subtitle="Gestión de llamadas telefónicas"
      />

      {/* 🚫 Si el usuario no tiene acceso a Twilio, mostrar mensaje */}
      {!userHasTwilioAccess ? (
        <div className={styles.contentContainer}>
          <div className={styles.noAccessMessage}>
            <h3>Acceso Restringido</h3>
            <p>No tienes permisos para acceder a la funcionalidad de llamadas.</p>
            <p>Contacta con tu administrador si crees que esto es un error.</p>
          </div>
        </div>
      ) : (
        <div className={styles.contentContainer}>
          {/* Sección de nueva llamada a la izquierda */}
          <div className={styles.callSection}>
            <h3 className={styles.sectionTitle}>
              {hasActiveCall ? `Llamada en curso con ${getCurrentContact()?.name || getCurrentContact()?.contactName || "contacto"}` : 
               "Realizar llamada"}
            </h3>

            {/* Selector de contacto */}
            {!hasActiveCall && (
              <div className={styles.contactSelector}>
                {loadingContacts ? (
                  <div className={styles.loadingContacts}>
                    <div className={styles.loadingSpinner}></div>
                    <span>Cargando contactos...</span>
                  </div>
                ) : (
                  <>
                    <Dropdown
                      options={contactsList}
                      selectedOption={selectedContact}
                      onSelect={handleContactSelect}
                      placeholder="Seleccione un contacto..."
                      className={styles.contactDropdown}
                    />
                    
                    {showCustomInput && (
                      <div className={styles.customNumberInput}>
                        <Input
                          placeholder={selectedContact?.isAgent ? 
                            "Ingresa el número a llamar (los agentes son solo referencia)" : 
                            "Número de teléfono"
                          }
                          value={customNumber}
                          onChange={(e) => setCustomNumber(e.target.value)}
                          className={styles.numberInput}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Timer de llamada */}
            {hasActiveCall && (
              <div className={styles.callTimer}>
              <div className={styles.timerDisplay}>
                {formatDuration(callDuration)}
              </div>
            </div>
          )}

          {/* Animación de conexión - ELIMINADA: Ahora usamos el panel flotante */}

          {/* Controles de llamada */}
          <div className={styles.callControls}>
            {!hasActiveCall && (
              <>
                <Button
                  variant="primary"
                  onClick={handleStartCall}
                  className={styles.callButton}
                  disabled={!getCurrentContact() || !isDeviceReady}
                >
                  {!isDeviceReady ? "Conectando servicio..." : "Llamar"}
                </Button>
                
                <Button
                  variant="secondary"
                  onClick={handleOpenAgentModal}
                  className={styles.callButton}
                  disabled={!isDeviceReady}
                  style={{ marginLeft: '10px' }}
                >
                  📞 Llamar a compañera
                </Button>
              </>
            )}

            {/* Botón Colgar eliminado - Se usa el panel flotante de la derecha */}

            {/* Botón cancelar eliminado - Ahora se usa el panel flotante */}
          </div>
        </div>

        {/* Registro de llamadas Twilio a la derecha */}
        <div className={styles.tableSection}>
          <TwilioCallLogs />
        </div>
      </div>
      )}
      
      {/* Modal para llamadas internas entre agentes */}
      <AgentSelectionModal
        open={showAgentModal}
        agents={availableAgents}
        onSelectAgent={handleInternalCall}
        onCancel={() => setShowAgentModal(false)}
        mode="call"
      />
    </div>
  );
};

export default CallsPage;